import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
    completeMainTaskSprint,
    createMainTaskSprint,
    deleteMainTaskSprint,
    fetchMainTaskEpics,
    fetchMainTaskSprintById,
    fetchMainTaskSprintSummaryById,
    fetchMainTaskSprints,
    normalizeMainTaskSprintDetailPayload,
    normalizeMainTaskSprintSummaryPayload,
    moveMainTask,
    updateMainTaskEpic,
    updateMainTaskEpicPosition,
    updateMainTaskSprint,
} from './developmentSprint.service'

const initialState = {
    status: 'idle',
    error: null,
    epicsRaw: [],
    sprintsRaw: [],
    lastProjectId: null,
    sprintDetail: null,
    sprintDetailId: null,
    sprintDetailStatus: 'idle',
    sprintDetailError: null,
    sprintSummary: null,
    sprintSummarySprintId: null,
    sprintSummaryStatus: 'idle',
    sprintSummaryError: null,
}

export const loadDevelopmentSprintBoard = createAsyncThunk(
    'developmentSprint/loadBoard',
    async (
        { projectId, sprintQuery = {}, epicQuery = {} },
        { rejectWithValue }
    ) => {
        if (!projectId) return rejectWithValue('Missing projectId')
        try {
            const epicPayload = {
                projectId,
                ...epicQuery,
            }
            const sprintPayload = { projectId, ...sprintQuery }
            const [epRes, spRes] = await Promise.all([
                fetchMainTaskEpics(epicPayload),
                fetchMainTaskSprints(sprintPayload),
            ])
            const epBody = epRes?.data ?? {}
            const spBody = spRes?.data ?? {}
            const epicsRaw = Array.isArray(epBody.data)
                ? epBody.data
                : Array.isArray(epBody)
                  ? epBody
                  : []
            const sprintsRaw = Array.isArray(spBody.data)
                ? spBody.data
                : Array.isArray(spBody)
                  ? spBody
                  : []
            return {
                projectId,
                epicsRaw,
                sprintsRaw,
            }
        } catch (error) {
            return rejectWithValue(
                error?.response?.data ?? error?.message ?? 'Request failed'
            )
        }
    }
)

export const updateMainTaskSprintThunk = createAsyncThunk(
    'developmentSprint/updateSprint',
    async (
        { sprintId, title, start, end, isActive, isComplete },
        { rejectWithValue }
    ) => {
        if (!sprintId) {
            return rejectWithValue('Missing sprintId')
        }
        try {
            const res = await updateMainTaskSprint({
                sprintId,
                title,
                start,
                end,
                isActive,
                isComplete,
            })
            return res?.data ?? res
        } catch (error) {
            return rejectWithValue(
                error?.response?.data ?? error?.message ?? 'Request failed'
            )
        }
    }
)

export const createMainTaskSprintThunk = createAsyncThunk(
    'developmentSprint/createSprint',
    async ({ projectId, title, start, end }, { rejectWithValue }) => {
        if (!projectId || !title) {
            return rejectWithValue('Missing projectId or title')
        }
        try {
            const res = await createMainTaskSprint({
                projectId,
                title,
                start,
                end,
            })
            return res?.data ?? res
        } catch (error) {
            return rejectWithValue(
                error?.response?.data ?? error?.message ?? 'Request failed'
            )
        }
    }
)

export const fetchMainTaskSprintDetailThunk = createAsyncThunk(
    'developmentSprint/fetchSprintDetail',
    async ({ sprintId }, { rejectWithValue }) => {
        if (!sprintId) {
            return rejectWithValue('Missing sprintId')
        }
        try {
            const res = await fetchMainTaskSprintById(sprintId)
            const body = res?.data ?? res
            const doc = normalizeMainTaskSprintDetailPayload(body)
            if (!doc || typeof doc !== 'object') {
                return rejectWithValue('Invalid sprint detail response')
            }
            return { sprintId: String(sprintId), doc }
        } catch (error) {
            return rejectWithValue(
                error?.response?.data ?? error?.message ?? 'Request failed'
            )
        }
    }
)

export const fetchMainTaskSprintSummaryThunk = createAsyncThunk(
    'developmentSprint/fetchSprintSummary',
    async ({ sprintId }, { rejectWithValue }) => {
        if (!sprintId) {
            return rejectWithValue('Missing sprintId')
        }
        try {
            const res = await fetchMainTaskSprintSummaryById(sprintId)
            const body = res?.data ?? res
            const summary = normalizeMainTaskSprintSummaryPayload(body)
            if (!summary || typeof summary !== 'object') {
                return rejectWithValue('Invalid sprint summary response')
            }
            return { sprintId: String(sprintId), summary }
        } catch (error) {
            return rejectWithValue(
                error?.response?.data ?? error?.message ?? 'Request failed'
            )
        }
    }
)

export const completeMainTaskSprintThunk = createAsyncThunk(
    'developmentSprint/completeSprint',
    async ({ sprintId, moveOpenTasksTo, newSprint }, { rejectWithValue }) => {
        if (!sprintId || !moveOpenTasksTo) {
            return rejectWithValue('Missing sprintId or moveOpenTasksTo')
        }
        try {
            const res = await completeMainTaskSprint({
                sprintId,
                moveOpenTasksTo,
                newSprint,
            })
            return res?.data ?? res
        } catch (error) {
            return rejectWithValue(
                error?.response?.data ?? error?.message ?? 'Request failed'
            )
        }
    }
)

export const deleteMainTaskSprintThunk = createAsyncThunk(
    'developmentSprint/deleteSprint',
    async ({ sprintId, moveTasksTo }, { rejectWithValue }) => {
        if (!sprintId || !moveTasksTo) {
            return rejectWithValue('Missing sprintId or moveTasksTo')
        }
        try {
            const res = await deleteMainTaskSprint({ sprintId, moveTasksTo })
            return res?.data ?? res
        } catch (error) {
            return rejectWithValue(
                error?.response?.data ?? error?.message ?? 'Request failed'
            )
        }
    }
)

export const moveMainTaskThunk = createAsyncThunk(
    'developmentSprint/moveMainTask',
    async (
        {
            taskId,
            projectId,
            sprintId,
            status,
            initialPosition,
            position,
            epic,
        },
        { rejectWithValue }
    ) => {
        if (!taskId || !projectId) {
            return rejectWithValue('Missing taskId or projectId')
        }
        try {
            const res = await moveMainTask({
                taskId,
                projectId,
                sprintId,
                status,
                initialPosition,
                position,
                epic,
            })
            return res?.data ?? res
        } catch (error) {
            return rejectWithValue(
                error?.response?.data ?? error?.message ?? 'Request failed'
            )
        }
    }
)

export const updateMainTaskEpicThunk = createAsyncThunk(
    'developmentSprint/updateEpic',
    async ({ epicId, projectId, title, description }, { rejectWithValue }) => {
        if (!epicId || !projectId) {
            return rejectWithValue('Missing epicId or projectId')
        }
        try {
            const res = await updateMainTaskEpic({
                epicId,
                projectId,
                title,
                description,
            })
            return res?.data ?? res
        } catch (error) {
            return rejectWithValue(
                error?.response?.data ?? error?.message ?? 'Request failed'
            )
        }
    }
)

export const updateMainTaskEpicPositionThunk = createAsyncThunk(
    'developmentSprint/updateEpicPosition',
    async ({ epicId, projectId, position }, { rejectWithValue }) => {
        if (!epicId || !projectId) {
            return rejectWithValue('Missing epicId or projectId')
        }
        try {
            const res = await updateMainTaskEpicPosition({
                epicId,
                projectId,
                position,
            })
            return res?.data ?? res
        } catch (error) {
            return rejectWithValue(
                error?.response?.data ?? error?.message ?? 'Request failed'
            )
        }
    }
)

const developmentSprintSlice = createSlice({
    name: 'developmentSprint',
    initialState,
    reducers: {
        clearDevelopmentSprintBoard: (state) => {
            state.status = 'idle'
            state.error = null
            state.epicsRaw = []
            state.sprintsRaw = []
            state.lastProjectId = null
            state.sprintDetail = null
            state.sprintDetailId = null
            state.sprintDetailStatus = 'idle'
            state.sprintDetailError = null
            state.sprintSummary = null
            state.sprintSummarySprintId = null
            state.sprintSummaryStatus = 'idle'
            state.sprintSummaryError = null
        },
        clearMainTaskSprintDetail: (state) => {
            state.sprintDetail = null
            state.sprintDetailId = null
            state.sprintDetailStatus = 'idle'
            state.sprintDetailError = null
        },
        clearMainTaskSprintSummary: (state) => {
            state.sprintSummary = null
            state.sprintSummarySprintId = null
            state.sprintSummaryStatus = 'idle'
            state.sprintSummaryError = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadDevelopmentSprintBoard.pending, (state) => {
                state.status = 'loading'
                state.error = null
            })
            .addCase(loadDevelopmentSprintBoard.fulfilled, (state, action) => {
                state.status = 'succeeded'
                state.epicsRaw = action.payload.epicsRaw
                state.sprintsRaw = action.payload.sprintsRaw
                state.lastProjectId = action.payload.projectId
            })
            .addCase(loadDevelopmentSprintBoard.rejected, (state, action) => {
                state.status = 'failed'
                state.error =
                    typeof action.payload === 'string'
                        ? action.payload
                        : (action.error?.message ??
                          'Failed to load sprint board')
            })
            .addCase(fetchMainTaskSprintDetailThunk.pending, (state) => {
                state.sprintDetailStatus = 'loading'
                state.sprintDetailError = null
            })
            .addCase(
                fetchMainTaskSprintDetailThunk.fulfilled,
                (state, action) => {
                    state.sprintDetailStatus = 'succeeded'
                    state.sprintDetail = action.payload.doc
                    state.sprintDetailId = action.payload.sprintId
                    state.sprintDetailError = null
                }
            )
            .addCase(
                fetchMainTaskSprintDetailThunk.rejected,
                (state, action) => {
                    state.sprintDetailStatus = 'failed'
                    state.sprintDetail = null
                    state.sprintDetailId = null
                    state.sprintDetailError =
                        typeof action.payload === 'string'
                            ? action.payload
                            : (action.error?.message ?? 'Failed to load sprint')
                }
            )
            .addCase(fetchMainTaskSprintSummaryThunk.pending, (state) => {
                state.sprintSummaryStatus = 'loading'
                state.sprintSummaryError = null
            })
            .addCase(
                fetchMainTaskSprintSummaryThunk.fulfilled,
                (state, action) => {
                    state.sprintSummaryStatus = 'succeeded'
                    state.sprintSummary = action.payload.summary
                    state.sprintSummarySprintId = action.payload.sprintId
                    state.sprintSummaryError = null
                }
            )
            .addCase(
                fetchMainTaskSprintSummaryThunk.rejected,
                (state, action) => {
                    state.sprintSummaryStatus = 'failed'
                    state.sprintSummary = null
                    state.sprintSummarySprintId = null
                    state.sprintSummaryError =
                        typeof action.payload === 'string'
                            ? action.payload
                            : (action.error?.message ??
                              'Failed to load sprint summary')
                }
            )
    },
})

export const {
    clearDevelopmentSprintBoard,
    clearMainTaskSprintDetail,
    clearMainTaskSprintSummary,
} = developmentSprintSlice.actions

export const selectDevelopmentSprintState = (state) => state.developmentSprint

export default developmentSprintSlice.reducer
